import type { DatumUsage, DatumVerticalUsageType } from "building-types/datums/datumUsageTypes";
import type { DocEntityParentId } from "building-types/doc/docEntityConstraintTypes";
import type { PrimaryEntityId } from "building-types/doc/primaryEntityId";
import type { DocEntityBase } from "building-types/entities/docEntityBase";
import type { LabelableEntityBase } from "building-types/entities/labelTypes";
import type { CeilingHandle } from "building-types/handles/docEntityHandleTypes";
import type { InteriorMaterial } from "building-types/materials/interiorPaletteTypes";
import type { PaletteMaterialSlot } from "building-types/materials/paletteMaterialSlotTypes";
import type { FormulaDisabled } from "building-types/options/formulaDisabled";
import type { MaterialSrc } from "common/materials/materialSrc";
import type { Face } from "geom/brep";
import type { PolyLine2 } from "geom/polyLine2Types";
import type { ForeignEntityId } from "../handles/foreignEntityId";

export type Ceiling = (TraySimpleOffset | TrayFreeform | Vaulted | None | Flat) &
  DocEntityBase<"CEILING"> & {
    readonly id: PrimaryEntityId<"CEILING">;
    readonly parentId: DocEntityParentId<"ROOM">;
  };

export enum CeilingType {
  TRAY_SIMPLE_OFFSET = "TRAY_SIMPLE_OFFSET",
  TRAY_FREEFORM = "TRAY_FREEFORM",
  VAULTED = "VAULTED",
  NONE = "NONE",
  FLAT = "FLAT",
}

export enum CeilingBoundaryType {
  INSIDE_OF_FRAMING = "INSIDE_OF_FRAMING",
  MAX_RECTANGLE = "MAX_RECTANGLE",
}

export enum CeilingRidgeOrientation {
  AUTO = "AUTO",
  HORIZONTAL = "HORIZONTAL",
  VERTICAL = "VERTICAL",
  PARALLEL_TO_WALL = "PARALLEL_TO_WALL",
}

export enum HeightType {
  OFFSET_FROM_CEILING = "OFFSET_FROM_CEILING",
  ABOVE_FINISH_FLOOR = "ABOVE_FINISH_FLOOR",
}

export interface CeilingBase extends LabelableEntityBase, FormulaDisabled {
  readonly ceilingMaterial?: InteriorMaterial;
  readonly ceilingDatum?: DatumUsage<DatumVerticalUsageType.CEILING>;
}

export interface TraySimpleOffset extends CeilingBase {
  readonly heightType: HeightType;
  readonly height: number;
  readonly type: CeilingType.TRAY_SIMPLE_OFFSET;
  readonly boundaryType: CeilingBoundaryType;
  readonly offset: number;
}

export interface TrayFreeform extends CeilingBase {
  readonly heightType: HeightType;
  readonly height: number;
  readonly type: CeilingType.TRAY_FREEFORM;
  readonly outline: PolyLine2 | null;
}

export interface Vaulted extends CeilingBase {
  readonly type: CeilingType.VAULTED;
  readonly boundaryType: CeilingBoundaryType;
  readonly offset: number;
  readonly slope?: number;
  /**
   * normalized, 0.5 default
   */
  readonly ridge?: number;
  readonly ridgeOrientation?: CeilingRidgeOrientation;
  /**
   * refers to the wall associated with a
   * parent Room's edge number
   */
  readonly ridgeOrientingWall?: string;
}

export interface Flat extends CeilingBase {
  readonly type: CeilingType.FLAT;
}

export interface None extends CeilingBase {
  readonly type: CeilingType.NONE;
  readonly outline: PolyLine2 | null;
  readonly boundaryType: CeilingBoundaryType;
  readonly offset: number;
}

export interface GeneratedCeiling {
  readonly section: CeilingSection;
  readonly subCeilings: GeneratedSubCeiling[];
}

export enum CeilingOutlineError {
  OUTLINE_UNKNOWN_ERROR = "OUTLINE_UNKNOWN_ERROR",
  OUTLINE_NOT_DRAWN = "OUTLINE_NOT_DRAWN",
  OUTLINE_INTERSECTS_ROOM_OUTLINE = "OUTLINE_INTERSECTS_ROOM_OUTLINE",
  OUTLINE_OUTSIDE_OF_ROOM = "OUTLINE_OUTSIDE_OF_ROOM",
  OUTLINE_ZERO_AREA = "OUTLINE_ZERO_AREA",
  OUTLINE_SELF_INTERSECTING = "OUTLINE_SELF_INTERSECTING",
}

export const CeilingOutlineErrorMessage: { [id: string]: string } = {
  [CeilingOutlineError.OUTLINE_UNKNOWN_ERROR]: "Unknown error",
  [CeilingOutlineError.OUTLINE_NOT_DRAWN]: "Outline which is not drawn",
  [CeilingOutlineError.OUTLINE_INTERSECTS_ROOM_OUTLINE]: "Outline which intersects room outline",
  [CeilingOutlineError.OUTLINE_OUTSIDE_OF_ROOM]: "Outline which is outside of room",
  [CeilingOutlineError.OUTLINE_ZERO_AREA]: "Outline which has zero area",
  [CeilingOutlineError.OUTLINE_SELF_INTERSECTING]: "Outline which is self-intersecting",
};

export interface GeneratedSubCeiling extends LabelableEntityBase {
  readonly entityType: "CEILING";
  readonly handle: CeilingHandle;
  readonly integerId: number;
  readonly ceilingOutlineError: CeilingOutlineError | undefined;
  readonly outline: PolyLine2 | undefined;
  readonly sections: CeilingSection[];
  readonly type: CeilingType;
}

export interface CeilingSection {
  readonly ceilingFaces: Face[];
  readonly shadowFace?: Face;
  readonly materialSlot: PaletteMaterialSlot;
  readonly designMaterialId: MaterialSrc | undefined;
  readonly roomId: ForeignEntityId<"ROOM">;
}
